import { marked } from 'marked'
import DOMPurify from 'dompurify'

/**
 * Import Service for handling file imports
 * 
 * Handles file selection, reading, and conversion of .txt and .md files
 * to Quill-compatible format for opening in new tabs.
 */
class ImportService {
  /**
   * Open file dialog and handle file selection
   */
  async openFileDialog() {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.txt,.md'
      input.multiple = true
      input.style.display = 'none'
      
      input.onchange = (event) => {
        const files = Array.from(event.target.files)
        resolve(files)
        document.body.removeChild(input)
      }
      
      input.oncancel = () => {
        resolve([])
        document.body.removeChild(input)
      }
      
      document.body.appendChild(input)
      input.click()
    })
  }

  /**
   * Read file content as text
   */
  async readFileContent(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = (e) => reject(new Error('Failed to read file'))
      reader.readAsText(file, 'UTF-8')
    })
  }

  /**
   * Convert markdown content to Quill HTML format
   */
  convertMarkdownToQuill(markdownContent) {
    try {
      // Parse markdown to HTML
      const htmlContent = this.markdownToHtml(markdownContent)
      
      // Convert to Quill-compatible format
      return this.htmlToQuillFormat(htmlContent)
    } catch (error) {
      console.error('Markdown conversion failed:', error)
      // Fallback to plain text wrapped in paragraph
      return `<p>${markdownContent.replace(/\n/g, '<br>')}</p>`
    }
  }

  /**
   * Convert markdown to HTML using marked.js
   */
  markdownToHtml(markdown) {
    // Configure marked options
    marked.setOptions({
      breaks: true,
      gfm: true,
      sanitize: false, // We'll sanitize with DOMPurify
      smartLists: true,
      smartypants: true
    })

    return marked.parse(markdown)
  }

  /**
   * Convert HTML to Quill-compatible format
   */
  htmlToQuillFormat(html) {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      // Server-side fallback - return basic HTML
      return html
    }

    // Sanitize HTML for security
    const sanitizedHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'a', 'img',
        'div', 'span'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style']
    })

    // Create temporary DOM element for processing
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = sanitizedHtml
    
    // Process different elements for Quill compatibility
    this.processHeadings(tempDiv)
    this.processLists(tempDiv)
    this.processCodeBlocks(tempDiv)
    this.processLinks(tempDiv)
    this.processImages(tempDiv)
    this.processBoldItalic(tempDiv)
    this.processLineBreaks(tempDiv)
    
    return tempDiv.innerHTML
  }

  /**
   * Convert markdown headings to Quill format
   */
  processHeadings(element) {
    const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6')
    headings.forEach(heading => {
      const level = parseInt(heading.tagName.charAt(1))
      heading.setAttribute('data-level', level)
      heading.className = `ql-header-${level}`
    })
  }

  /**
   * Convert markdown lists to Quill format
   */
  processLists(element) {
    // Process unordered lists
    const ulElements = element.querySelectorAll('ul')
    ulElements.forEach(ul => {
      ul.className = 'ql-list ql-list-bullet'
    })
    
    // Process ordered lists
    const olElements = element.querySelectorAll('ol')
    olElements.forEach(ol => {
      ol.className = 'ql-list ql-list-ordered'
    })
  }

  /**
   * Convert markdown code blocks to Quill format
   */
  processCodeBlocks(element) {
    const codeBlocks = element.querySelectorAll('pre code')
    codeBlocks.forEach(codeBlock => {
      const pre = codeBlock.parentElement
      pre.className = 'ql-code-block'
      pre.setAttribute('data-language', this.detectLanguage(codeBlock))
    })
    
    // Process inline code
    const inlineCode = element.querySelectorAll('code:not(pre code)')
    inlineCode.forEach(code => {
      code.className = 'ql-code-inline'
    })
  }

  /**
   * Process links
   */
  processLinks(element) {
    const links = element.querySelectorAll('a')
    links.forEach(link => {
      link.className = 'ql-link'
    })
  }

  /**
   * Process images
   */
  processImages(element) {
    const images = element.querySelectorAll('img')
    images.forEach(img => {
      img.className = 'ql-image'
    })
  }

  /**
   * Process bold and italic text
   */
  processBoldItalic(element) {
    const boldElements = element.querySelectorAll('strong, b')
    boldElements.forEach(el => {
      el.className = 'ql-bold'
    })
    
    const italicElements = element.querySelectorAll('em, i')
    italicElements.forEach(el => {
      el.className = 'ql-italic'
    })
  }

  /**
   * Process line breaks
   */
  processLineBreaks(element) {
    const brElements = element.querySelectorAll('br')
    brElements.forEach(br => {
      br.className = 'ql-line-break'
    })
  }

  /**
   * Detect programming language from code block
   */
  detectLanguage(codeElement) {
    const className = codeElement.className
    const match = className.match(/language-(\w+)/)
    return match ? match[1] : 'text'
  }

  /**
   * Extract title from filename or content
   */
  extractTitle(filename, content) {
    // Remove file extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '')
    
    // If content starts with # heading, use that as title
    const headingMatch = content.match(/^#\s+(.+)$/m)
    if (headingMatch) {
      return headingMatch[1].trim()
    }
    
    // Otherwise use filename
    return nameWithoutExt || 'Imported Note'
  }

  /**
   * Validate file type and size
   */
  validateFile(file) {
    const maxSize = 10 * 1024 * 1024 // 10MB
    const allowedTypes = ['.txt', '.md']
    
    // Check file size
    if (file.size > maxSize) {
      throw new Error(`File "${file.name}" is too large. Maximum size is 10MB.`)
    }
    
    // Check file extension
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    if (!allowedTypes.includes(fileExt)) {
      throw new Error(`File "${file.name}" is not supported. Please select .txt or .md files.`)
    }
    
    return true
  }

  /**
   * Process multiple files and create note data
   */
  async processFiles(files) {
    const notes = []
    const errors = []
    
    for (const file of files) {
      try {
        // Validate file
        this.validateFile(file)
        
        // Read file content
        const content = await this.readFileContent(file)
        
        // Skip empty files
        if (!content.trim()) {
          console.warn(`Skipping empty file: ${file.name}`)
          continue
        }
        
        const isMarkdown = file.name.toLowerCase().endsWith('.md')
        
        let quillContent
        if (isMarkdown) {
          quillContent = this.convertMarkdownToQuill(content)
        } else {
          // For .txt files, wrap in paragraph tags
          quillContent = `<p>${content.replace(/\n/g, '<br>')}</p>`
        }
        
        const title = this.extractTitle(file.name, content)
        
        notes.push({
          title,
          content: quillContent,
          originalFilename: file.name,
          fileType: isMarkdown ? 'markdown' : 'text',
          fileSize: file.size,
          importDate: new Date().toISOString()
        })
        
      } catch (error) {
        console.error(`Failed to process file ${file.name}:`, error)
        errors.push({
          filename: file.name,
          error: error.message
        })
      }
    }
    
    return { notes, errors }
  }
}

export default new ImportService()
